import DeleteDialog from 'core/components/shared/DeleteDialog';
import { Utils } from 'core/utils';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ListToolbar from 'cms/components/ListToolbar';
import { UseCompanyList } from '../hooks';
import { JobHttpService } from '../service';
import AdminCompanyEditDialog from './dialogs/AdminCompanyEditDialog';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

const initialFilters = {
    title: '',
    city: '',
    country: ''
};

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const CompanyList = () => {
    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const { data, isLoading, isFetching, refetch, removeItemFromList } = UseCompanyList({
        size: 100,
        filters: appliedFilters
    });

    const companies = data?.results ?? [];

    const filteredCompanies = useMemo(() => {
        const normalizedFilters = Object.entries(appliedFilters).reduce((acc, [key, value]) => {
            acc[key] = value ? value.toLowerCase() : '';
            return acc;
        }, {});

        return companies.filter((company) => {
            const title = formatText(company.title, '').toLowerCase();
            const city = formatText(company.city, '').toLowerCase();
            const country = formatText(company.country?.title ?? '', '').toLowerCase();

            const matchesTitle = !normalizedFilters.title || title.includes(normalizedFilters.title);
            const matchesCity = !normalizedFilters.city || city.includes(normalizedFilters.city);
            const matchesCountry = !normalizedFilters.country || country.includes(normalizedFilters.country);

            return matchesTitle && matchesCity && matchesCountry;
        });
    }, [companies, appliedFilters]);

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
    };

    const handleDeleteRequest = (companyId) => {
        setSelectedId(companyId);
        setDeleteDialogIsOpen(true);
    };

    const handleDeleteConfirmed = () => {
        setDeleteDialogIsOpen(false);
        if (selectedId) {
            removeItemFromList(selectedId);
        }
    };

    const handleCreateCompany = async (payload) => {
        await JobHttpService.createCompany(payload);
        await refetch();
    };

    const activeFilterEntries = Object.entries(appliedFilters).filter(([, value]) => Boolean(value));

    return (
        <Stack spacing={2}>
            <ListToolbar
                title="Companies"
                subtitle={`${filteredCompanies.length} compan${filteredCompanies.length === 1 ? 'y' : 'ies'}`}
                actions={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateDialogOpen(true)}>
                        Add New Company
                    </Button>
                }
                search={{
                    value: filters.title,
                    onChange: (value) => setFilters((prev) => ({ ...prev, title: value })),
                    onSubmit: handleApplyFilters,
                    onClear: () => setAppliedFilters((prev) => ({ ...prev, title: '' })),
                    placeholder: 'Search companies…'
                }}
                filterFields={
                    <Grid container spacing={1.5}>
                        {[['city', 'City'], ['country', 'Country']].map(([name, label]) => (
                            <Grid item xs={12} sm={6} md={3} key={name}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={label}
                                    name={name}
                                    value={filters[name]}
                                    onChange={handleFilterChange}
                                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                />
                            </Grid>
                        ))}
                    </Grid>
                }
                activeFilters={activeFilterEntries}
                onRemoveFilter={(key) => {
                    setAppliedFilters((prev) => ({ ...prev, [key]: '' }));
                    setFilters((prev) => ({ ...prev, [key]: '' }));
                }}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                onRefresh={() => refetch()}
                refreshing={isFetching}
            />

            <Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Website</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell title="Number of employees" >#</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredCompanies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No companies found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCompanies.map((company) => (
                                    <TableRow key={company.id}>
                                        <TableCell>{company.id}</TableCell>
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography fontWeight={600}>{formatText(company.title)}</Typography>
                                                
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {[
                                                formatText(company.city, ''),
                                                formatText(company.country?.title ?? '', '')
                                            ]
                                                .filter(Boolean)
                                                .join(', ') || '—'}
                                        </TableCell>
                                        <TableCell>
                                            {company.website ? (
                                                <a href={company.website} target="_blank" rel="noreferrer">
                                                    {company.website}
                                                </a>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {company.phone_number || company.manager_name
                                                ? `${company.phone_number ?? ''} ${company.manager_name ?? ''}`.trim()
                                                : '—'}
                                        </TableCell>
                                        <TableCell>{company.number_of_employees}</TableCell>
                                        <TableCell>{company.created_at ? Utils.getDate(company.created_at) : '—'}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Open details">
                                                    <IconButton color="primary"
                                                        size="small"
                                                        component={Link}
                                                        to={`/cms/company/${company.id}`}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete company">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteRequest(company.id)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <DeleteDialog
                url={`/job/company/${selectedId}`}
                isOpen={deleteDialogIsOpen}
                key={`company-delete-${deleteDialogIsOpen}-${selectedId}`}
                setOpen={setDeleteDialogIsOpen}
                notifyDone={handleDeleteConfirmed}
            />

            <AdminCompanyEditDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                company={null}
                onSave={handleCreateCompany}
            />
        </Stack>
    );
};

export default CompanyList;

